import React from 'react';
import {
  Box,
  Image,
  Badge,
  AspectRatio,
  Skeleton,
  useColorModeValue,
  VStack,
  Text,
  HStack,
  Wrap,
  WrapItem,
  Flex,
  useBreakpointValue,
  Tooltip,
  Icon
} from '@chakra-ui/react';
import { ImageSquare, VideoCamera, Article, Cube, MapPin } from '@phosphor-icons/react';
import { getMediaFeatures } from '../../utils/featureUtils';
import ThreeDViewer from '../ThreeDViewer';

// Helper function to check if media has features
const hasFeatures = (media) => {
  return media.predictions && media.predictions.length > 0;
};

// Get features list with proper mapping and safety checks
const getFeaturesList = (media) => {
  if (!media) return [];
  try {
    return getMediaFeatures(media)
      .sort((a, b) => (b.confidence || 0) - (a.confidence || 0) || 
                      (a.label || '').localeCompare(b.label || ''));
  } catch (error) {
    console.warn('Error getting features for media:', error);
    return [];
  }
};

// Format feature label for display
const formatFeatureLabel = (label) => {
  return String(label)
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
};

export default function MediaThumbnail({ media, index, onClick, showThumbnailTags, onTagClick, activeTag }) {
  const hasLocation = Boolean(media.coordinates || media.subLocationId);
  const isVideo = media.type === 'video';
  const isArticle = media.type === 'article';
  const is3D = media.type === '3d';
  
  // Adventure Journal theme colors - using proper theme tokens
  const cardBg = useColorModeValue('#E6DAC0', '#38291E'); // bg.surface
  const cardHoverBg = useColorModeValue('#D9CAA7', '#483123'); // bg.accent
  const borderColor = useColorModeValue('#9C826B', '#6A4E33'); // border.default
  const videoBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold) for videos
  const articleBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold) for articles
  const threeDModelBg = useColorModeValue('#6B46C1', '#9F7AEA'); // Purple for 3D models
  const descriptionColor = "whiteAlpha.900"; // High contrast overlay text
  const descriptionBg = "rgba(0,0,0,0.8)"; // Darker overlay for better readability
  const locationBadgeBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const locationBadgeColor = "white"; // High contrast
  const autoBadgeBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const manualBadgeBg = useColorModeValue('#C19434', '#D6AD60'); // interactive.primary (gold)
  const badgeTextColor = "white"; // High contrast
  
  // Get features 
  const features = getFeaturesList(media);
  
  // More compact sizing for better grid layout
  const iconSize = useBreakpointValue({ base: 36, sm: 40, md: 48 }) || 40; // Smaller icons
  const thumbWidth = useBreakpointValue({ base: "100%", sm: "100%", md: "100%" }) || "100%"; // Full width for responsive grid
  const thumbHeight = useBreakpointValue({ base: "200px", sm: "220px", md: "240px", lg: "260px" }) || "220px"; // More compact heights
  const badgeSize = useBreakpointValue({ base: 'sm', md: 'md' }) || 'sm'; // Smaller badges
  const fontSize = useBreakpointValue({ base: 'sm', md: 'md' }) || 'sm'; // Smaller text
  const aspectRatio = useBreakpointValue({ base: 4/3, sm: 4/3, md: 4/3, lg: 4/3 }) || 4/3; // Consistent photo ratios

  return (
    <Box
      onClick={() => onClick(index)}
      cursor="pointer"
      borderRadius="xl"
      overflow="hidden"
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="md"
      _hover={{
        transform: { base: 'scale(1.02)', md: 'translateY(-4px)' }, // Scale on mobile, translate on desktop
        boxShadow: 'xl',
        bg: cardHoverBg,
        borderColor: useColorModeValue('#C19434', '#D6AD60')
      }}
      _active={{
        transform: { base: 'scale(0.98)', md: 'translateY(-2px)' }, // Scale feedback on mobile
        transition: 'transform 0.1s ease'
      }}
      role="group"
      position="relative"
      width={thumbWidth}
      height={thumbHeight}
      minW={{ base: "150px", sm: "180px", md: "200px" }} // More compact minimum sizes
      minH={{ base: "200px", sm: "220px", md: "240px" }}
      maxW={{ base: "300px", sm: "350px", md: "400px", lg: "450px" }} // More compact maximum sizes
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      className="media-thumbnail"
    >
      <Box position="relative" height="100%">
        <AspectRatio ratio={aspectRatio} height="100%">
          <Box>
            {isVideo ? (
              <Flex
                align="center"
                justify="center"
                bg={videoBg}
                color="white"
                width="100%"
                height="100%"
                direction="column"
                gap={2}
              >
                <Icon as={VideoCamera} boxSize={iconSize} weight="thin" />
                <Badge colorScheme="brand.secondary" px={2} py={1} borderRadius="md">Video</Badge> {/* Use theme scheme */}
              </Flex>
            ) : isArticle ? (
              <Flex
                align="center"
                justify="center"
                bg={articleBg}
                color="white"
                width="100%"
                height="100%"
                direction="column"
                gap={2}
              >
                <Icon as={Article} boxSize={iconSize} weight="thin" />
                <Badge colorScheme="info" px={2} py={1} borderRadius="md">Article</Badge> {/* Use theme scheme */}
              </Flex>
            ) : is3D ? (
              media.url ? (
                <Box position="relative" width="100%" height="100%">
                  <ThreeDViewer
                    url={media.url}
                    fileName={media.name}
                    fileExtension={media.modelInfo?.type?.toLowerCase()}
                    width="100%"
                    height="100%"
                    showControls={false}
                    autoRotate={true}
                  />
                  <Box
                    position="absolute"
                    top={1}
                    left={1}
                    zIndex={1}
                  >
                    <Badge colorScheme="purple" px={2} py={1} borderRadius="md" fontSize="2xs">
                      {media.modelInfo?.type || '3D'}
                    </Badge>
                  </Box>
                </Box>
              ) : (
                <Flex
                  align="center"
                  justify="center"
                  bg={threeDModelBg}
                  color="white"
                  width="100%"
                  height="100%"
                  direction="column"
                  gap={2}
                >
                  <Icon as={Cube} boxSize={iconSize} weight="thin" />
                  <Badge colorScheme="purple" px={2} py={1} borderRadius="md">
                    {media.modelInfo?.type || '3D Model'}
                  </Badge>
                </Flex>
              )
            ) : (
              <Skeleton isLoaded={!!media.url} height="100%" width="100%">
                <Image
                  src={media.url}
                  alt={media.title || 'Media item'}
                  objectFit="cover"
                  width="100%"
                  height="100%"
                  borderRadius="md"
                />
              </Skeleton>
            )}
          </Box>
        </AspectRatio>

        {/* Location badge - Enhanced for mobile */}
        {hasLocation && (
          <Tooltip 
            label={media.subLocation?.name || "Has location data"} 
            placement="top" 
            hasArrow
            fontSize="sm"
            bg={useColorModeValue('#3B2E1E', '#E6D7B8')}
            color={useColorModeValue('#E6DAC0', '#2C231B')}
          >
            <Badge
              position="absolute"
              top={3}
              right={3}
              bg={locationBadgeBg}
              color={locationBadgeColor}
              px={3}
              py={2}
              borderRadius="full"
              display="flex"
              alignItems="center"
              gap={2}
              zIndex={2}
              fontSize={fontSize}
              fontWeight="semibold"
              minH="32px" // Better touch target
              _hover={{
                transform: "scale(1.05)",
                boxShadow: "lg"
              }}
              _active={{
                transform: "scale(0.95)"
              }}
              boxShadow="lg"
              transition="all 0.2s ease-in-out"
              backdropFilter="blur(4px)"
            >
              <Icon as={MapPin} weight="bold" boxSize={4} />
              {media.subLocation?.name && (
                <Text 
                  fontSize={fontSize}
                  display={{ base: 'none', sm: 'block' }}
                  fontFamily="Inter"
                  fontWeight="medium"
                  noOfLines={1}
                  maxW="100px"
                >
                  {media.subLocation.name}
                </Text>
              )}
            </Badge>
          </Tooltip>
        )}
      </Box>

      {/* Media Title and Tags Container */}
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        zIndex={2}
      >
        {/* Title for video/article/3D - but not showing filenames */}
        {(isVideo || isArticle || is3D) && media.title && (
          <Box
            w="100%"
            p={2}
            bg={descriptionBg}
            color={descriptionColor}
            backdropFilter="blur(8px)"
          >
            <Text fontSize={{ base: '2xs', md: 'xs' }} fontWeight="medium" noOfLines={1}> {/* Responsive font */}
              {media.title}
            </Text>
          </Box>
        )}
        
        {/* Feature tags - Enhanced for mobile */}
        {hasFeatures(media) && showThumbnailTags && (
          <Box
            w="100%"
            p={3}
            bg={useColorModeValue('rgba(230,218,192,0.95)', 'rgba(56,41,30,0.95)')}
            backdropFilter="blur(12px)"
            onClick={(e) => e.stopPropagation()}
            borderTop="1px solid"
            borderColor={useColorModeValue('rgba(156,130,107,0.3)', 'rgba(106,78,51,0.3)')}
          >
            <Wrap spacing={2} justify="center" align="center">
              {features.slice(0, 3).map((feature) => (
                <WrapItem key={feature.id}>
                  <Badge
                    onClick={() => onTagClick?.(feature.id)} 
                    cursor={onTagClick ? "pointer" : "default"}
                    bg={autoBadgeBg}
                    color={badgeTextColor}
                    borderRadius="full"
                    px={3}
                    py={1}
                    minH="28px" // Better touch target
                    display="flex"
                    alignItems="center"
                    fontSize={fontSize}
                    fontWeight="semibold"
                    fontFamily="Inter"
                    boxShadow="sm"
                    _hover={onTagClick ? {
                      transform: 'translateY(-2px)',
                      boxShadow: 'md',
                      bg: useColorModeValue('#A67C42', '#E6C680')
                    } : {}}
                    _active={onTagClick ? {
                      transform: 'translateY(0)'
                    } : {}}
                    transition="all 0.2s ease-in-out"
                  >
                    {formatFeatureLabel(feature.label)}
                  </Badge>
                </WrapItem>
              ))}
              {features.length > 3 && (
                <WrapItem>
                  <Tooltip 
                    label={`${features.length - 3} more features`} 
                    hasArrow
                    fontSize="sm"
                  >
                    <Badge 
                      bg={useColorModeValue('#6B573B', '#B7A58B')}
                      color="white"
                      borderRadius="full" 
                      px={3}
                      py={1}
                      minH="28px"
                      display="flex"
                      alignItems="center"
                      fontSize={fontSize}
                      fontWeight="medium"
                      fontFamily="Inter"
                      boxShadow="sm"
                      _hover={{ 
                        transform: 'scale(1.05)',
                        boxShadow: 'md'
                      }}
                      transition="all 0.2s ease-in-out"
                    >
                      +{features.length - 3}
                    </Badge>
                  </Tooltip>
                </WrapItem>
              )}
            </Wrap>
          </Box>
        )}
      </Box>
    </Box>
  );
}
